import type { ProjectPlan } from '../../../../shared/types/plans';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';

/**
 * UX-08: the wizard collapsed from 8 steps to 4 — Welcome, Your codebase
 * (stack + scale + needs + plan), Connect GitHub (+ optional BYOK),
 * First epic.
 */
export type WizardStep = 0 | 1 | 2 | 3;

/** Bumped from the unversioned pre-UX-08 (8-step) draft shape. */
export const DRAFT_VERSION = 2;

export interface WizardDraft {
  v: typeof DRAFT_VERSION;
  step: WizardStep;
  accountName: string;
  answers: OnboardingAnswers;
  chosenPlan: ProjectPlan;
  accountId: string | null;
  projectId: string | null;
}

/** Pre-UX-08 draft shape: `step` was 0..7 and carried no `v` field. */
interface WizardDraftV1 {
  step: number;
  accountName: string;
  answers: OnboardingAnswers;
  chosenPlan: ProjectPlan;
  accountId: string | null;
  projectId: string | null;
}

export const DEFAULT_ANSWERS: OnboardingAnswers = {
  stacks: {},
  repoCount: '1',
  orgCount: '1',
  projectCount: '1',
  needs: {
    testDeploys: false,
    browserSmoke: false,
    compliance: [],
    providers: [],
    ssoScim: false,
  },
};

export const DRAFT_KEY = 'forge.onboarding.draft';

/**
 * Maps an old 0..7 wizard step onto the new 0..3 one:
 *   0 (Welcome)                              → 0 (Welcome)
 *   1-4 (Tech stack / Scale / Needs / Plan)   → 1 (Your codebase)
 *   5-6 (API keys / GitHub)                   → 2 (Connect GitHub)
 *   7 (First epic)                            → 3 (First epic)
 */
function migrateV1Step(oldStep: number): WizardStep {
  if (oldStep <= 0) return 0;
  if (oldStep <= 4) return 1;
  if (oldStep <= 6) return 2;
  return 3;
}

function isCurrentVersion(parsed: unknown): parsed is WizardDraft {
  return !!parsed && typeof parsed === 'object' && (parsed as { v?: unknown }).v === DRAFT_VERSION;
}

export function loadDraft(): WizardDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (isCurrentVersion(parsed)) return parsed;

    // Unversioned (v1) draft from the 8-step wizard — migrate the step
    // number and carry over everything that still applies. A malformed or
    // unrecognisable draft resolves to sensible defaults rather than
    // throwing, so a corrupt localStorage entry never blocks signup.
    const v1 = parsed as Partial<WizardDraftV1>;
    return {
      v: DRAFT_VERSION,
      step: migrateV1Step(v1.step ?? 0),
      accountName: v1.accountName ?? '',
      answers: v1.answers ?? DEFAULT_ANSWERS,
      chosenPlan: v1.chosenPlan ?? 'team',
      accountId: v1.accountId ?? null,
      projectId: v1.projectId ?? null,
    };
  } catch {
    return null;
  }
}

export function saveDraft(draft: WizardDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch { /* quota */ }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch { /* */ }
}

/** FORGE-262: starter is not sold — floor any recommendation/choice at team. */
export function floorPlanAtTeam(plan: ProjectPlan): ProjectPlan {
  return plan === 'starter' ? 'team' : plan;
}
